import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { getStoredToken } from '@/lib/authCache';
// MegaphoneSimple se importaba sin usarse en ninguna parte, y el 31-08-2026 tumbo el
// build de Vercel: "'MegaphoneSimple' is not exported from '@phosphor-icons/react'".
// El paquete instalado aca si lo trae, por eso compilaba en local y no alla.
import { X, HandFist, Envelope, ChatCircle, Bell, Trophy } from '@phosphor-icons/react';

const playNotificationSound = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    // Two-tone notification chime
    const playTone = (freq, start, dur) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + start + dur);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur);
    };
    playTone(880, 0, 0.15);
    playTone(1100, 0.12, 0.2);
    playTone(1320, 0.28, 0.25);
  } catch {}
};

const ICONS = {
  event_invitation: { icon: Envelope, color: '#FF6B00', label: 'Invitacion' },
  like_received: { icon: HandFist, color: '#FF6B00', label: 'Like' },
  match_created: { icon: HandFist, color: '#FF6B00', label: 'Match' },
  match_approved: { icon: ChatCircle, color: '#FF6B00', label: 'Aprobado' },
  match_rejected: { icon: X, color: '#EF4444', label: 'Rechazado' },
  fight_result: { icon: Trophy, color: '#FF6B00', label: 'Resultado' },
};

const NotificationPopup = ({ notification, onClose, onAction }) => {
  const config = ICONS[notification.type] || { icon: Bell, color: '#FF6B00', label: 'Notificacion' };
  const Icon = config.icon;

  useEffect(() => {
    const timer = setTimeout(onClose, 8000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="animate-slide-in pointer-events-auto w-full max-w-sm bg-[#141414] border-2 border-[#FF6B00] shadow-[0_0_30px_rgba(255,107,0,0.3)] overflow-hidden"
      data-testid="notification-popup" role="alert">
      <div className="flex items-start gap-3 p-4">
        <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-full" style={{ backgroundColor: `${config.color}20` }}>
          <Icon size={22} weight="bold" style={{ color: config.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-wider" style={{ color: config.color }}>{config.label}</p>
          <p className="text-sm font-bold text-white mt-0.5 line-clamp-1">{notification.title}</p>
          <p className="text-xs text-[#A3A3A3] mt-0.5 line-clamp-2">{notification.message}</p>
        </div>
        <button onClick={onClose} className="text-[#888] hover:text-white flex-shrink-0 mt-1">
          <X size={16} weight="bold" />
        </button>
      </div>
      <button onClick={onAction}
        className="w-full bg-[#1a1a1a] hover:bg-[#1f1f1f] text-[#FF6B00] text-xs font-bold uppercase tracking-wider py-2 transition-colors border-t border-[#333333]">
        Ver detalle
      </button>
    </div>
  );
};

const requestNativePermission = async () => {
  if ('Notification' in window && Notification.permission === 'default') {
    await Notification.requestPermission();
  }
  // Register service worker for background notifications
  if ('serviceWorker' in navigator) {
    try {
      await navigator.serviceWorker.register('/sw.js');
    } catch {}
  }
};

const showNativeNotification = (notification) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    const config = ICONS[notification.type] || { label: 'Notificacion' };
    // Try service worker notification first (works in background)
    if (navigator.serviceWorker && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'SHOW_NOTIFICATION',
        title: notification.title || config.label,
        body: notification.message,
        tag: notification.notif_id,
      });
    } else {
      // Fallback to regular Notification API
      const n = new Notification(notification.title || config.label, {
        body: notification.message,
        icon: '/favicon.ico',
        tag: notification.notif_id,
        requireInteraction: false,
      });
      n.onclick = () => { window.focus(); n.close(); };
    }
  } catch {}
};

const NotificationPopupManager = () => {
  const navigate = useNavigate();
  const [popups, setPopups] = useState([]);
  const seenIdsRef = useRef(new Set());
  const initialLoadRef = useRef(true);

  useEffect(() => { requestNativePermission(); }, []);

  const checkNewNotifications = useCallback(async () => {
    if (!getStoredToken()) return;   // sin sesion no hay notificaciones que pedir
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      const unread = res.data.filter(n => !n.read);
      const newOnes = unread.filter(n => !seenIdsRef.current.has(n.notif_id));

      if (initialLoadRef.current) {
        unread.forEach(n => seenIdsRef.current.add(n.notif_id));
        initialLoadRef.current = false;
        return;
      }

      if (newOnes.length > 0) {
        playNotificationSound();
        newOnes.forEach(n => {
          seenIdsRef.current.add(n.notif_id);
          showNativeNotification(n);
        });
        setPopups(prev => [...prev, ...newOnes].slice(-3));
      }
    } catch {}
  }, []);

  useEffect(() => {
    checkNewNotifications();
    const interval = setInterval(checkNewNotifications, 90000);
    return () => clearInterval(interval);
  }, [checkNewNotifications]);

  const handleAction = (n) => {
    removePopup(n.notif_id);
    if (n.type === 'event_invitation' && n.event_id) navigate(`/events/${n.event_id}`);
    else if (n.type === 'match_approved' && n.match_id) navigate(`/chat/${n.match_id}`);
    else if (n.type === 'fight_result') navigate('/fight-history');
    else if (n.type === 'match_rejected') navigate('/swipe');
    else navigate('/my-matches');
  };

  const removePopup = (id) => {
    setPopups(prev => prev.filter(p => p.notif_id !== id));
  };

  if (popups.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-3 pointer-events-none" data-testid="popup-container">
      {popups.map(n => (
        <NotificationPopup key={n.notif_id} notification={n} onClose={() => removePopup(n.notif_id)} onAction={() => handleAction(n)} />
      ))}
    </div>
  );
};

export default NotificationPopupManager;
